import {
  postResource, getResource, deleteResource, putResource,
} from './handleFetch.service';
import { reportRefreshedToken } from '../utils/sessionEvents';

export const resetPassword = async (email) => await postResource('users/reset_password', { email });

export const setPassword = async (token, id, password) => await postResource(`users/reset_password/${token}`, { id, password });

// Offered on the login screen when the API says the address is not confirmed —
// otherwise that error is a dead end: the only confirmation link lives inside
// the message that never arrived.
export const resendConfirmation = async (email) => await postResource('users/email/resend-confirmation', { email }, null, { maxRetries: 0 });

export const confirmEmail = async (id) => await getResource(`users/email/confirm/${id}`);

// Every caller gets the sliding-session renewal for free: a `refreshedToken`
// in the reply is handed to AuthCookieSync, which swaps the stored token.
export const getUserInfo = async (token) => {
  const info = await getResource('users/info', token);
  reportRefreshedToken(info?.refreshedToken);
  return info;
};

export const setPromo = async (token, id, data) => await postResource(`users/set_promo/${id}`, { data }, token);

export const setPromoAdmin = async (token, userId, id, data) => await postResource(`users/set_promo_admin/${id}`, { userId, data }, token);

export const getUserSubscriptions = async (token) => getResource('users/subscriptions', token);

export const addSubscriptions = async (list, token) => await postResource('users/subscriptions/add', { list }, token);

export const addNewsSubscription = async (email) => await postResource('users/subscriptions/subscibe_news', { email });

export const removeNewsSubscription = async (email) => await postResource('users/subscriptions/unsubscribe', { email, type: 'newsletter' });

export const sendContactMessage = async (
  name,
  email,
  subject,
  message,
  captchaToken = null,
) => await postResource(
  'users/contact_email',
  {
    name, email, subject, message, captchaToken,
  },
);

export const authority = async (token) => await postResource('users/authority', {}, token);

export const saveOnboarding = async (data, token) => await postResource('users/onboarding', data, token);

export const skipOnboarding = async (token) => await postResource('users/onboarding/skip', {}, token);

export const setVisit = async (token) => await postResource('users/visit', {}, token);

export const getUserList = async (token) => getResource('users/users_list', token);

export const getUserSvgs = async (email, amount, token) => postResource('users/convertions', { amount, email }, token);

export const setCredits = async (id, amount, token) => await postResource('users/set_credits', { id, amount }, token);

export const setPurchasedStatus = async (id, hasPurchased, token) => await postResource('users/set_purchased_status', { id, hasPurchased }, token);

export const setPreviewCount = async (id, count, token) => await postResource('users/set_preview_count', { id, count }, token);

export const getGeoIp = async () => {
  try {
    // Use our own API route for reliable IP detection on Vercel
    const response = await fetch('/api/client-ip');
    if (!response.ok) {
      throw new Error('Failed to fetch IP');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching IP:', error);
    // Return fallback structure if API fails
    return { ip: null, IPv4: null };
  }
};

export const updateGeoIp = async (ip, token) => await postResource('users/geo', { ip }, token);

export const deleteUser = async (userId, token) => await deleteResource(`users/delete/${userId}`, token);

export const getPromoList = async (token) => await getResource('promo/list', token);

export const createPromo = async (promo, token) => await postResource('promo/add_item', { promo }, token);

export const deletePromo = async (promoCode, token) => await postResource('promo/delete_item', { promo: promoCode }, token);

export const assignPromoToUser = async (promoCode, userId, token) => await postResource('promo/assign_to_user', { promoCode, userId }, token);

export const unassignPromoFromUser = async (promoCode, userId, token) => await postResource('promo/unassign_from_user', { promoCode, userId }, token);

export const getAssignedUsers = async (promoCode, token) => await getResource(`promo/assigned_users/${promoCode}`, token);

// Personal API keys for the MCP server. Create and revoke are not idempotent,
// so they are never replayed on a network fault: a retried create could mint a
// second key the user never sees.
export const getApiKeys = async (token) => getResource('users/api-keys', token);

export const createApiKey = async (name, token) => postResource('users/api-keys', { name }, token, { maxRetries: 0 });

export const revokeApiKey = async (id, token) => deleteResource(`users/api-keys/${encodeURIComponent(id)}`, token, { maxRetries: 0 });

// Apps connected to the account through OAuth (ChatGPT, Claude, …). Client ids
// of metadata-document clients are URLs, hence the encoding. Disconnect is not
// replayed: a timeout means "no answer", and the list is reloaded afterwards.
export const getOAuthApps = async (token) => getResource('users/oauth-apps', token);

export const disconnectOAuthApp = async (clientId, token) => deleteResource(`users/oauth-apps/${encodeURIComponent(clientId)}`, token, { maxRetries: 0 });

// MCP usage of the signed-in user over the last `days` days (1..90).
export const getMcpUsage = async (token, days = 30) => getResource(`users/mcp-usage?days=${encodeURIComponent(days)}`, token);

// The user's own conversions, newest first, keyset-paginated by an opaque cursor.
export const getMyConversions = async (token, { limit = 20, cursor = null } = {}) => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return getResource(`converter/my?${query.toString()}`, token);
};

// Email preferences of the signed-in user: { marketingEmails, newsletter }.
export const getEmailPreferences = async (token) => getResource('users/email-preferences', token);

export const updateEmailPreferences = async (prefs, token) => putResource('users/email-preferences', prefs, token, { maxRetries: 0 });

// Revokes every session of the user, the current one included.
export const logoutAllSessions = async (token) => postResource('users/logout-all', {}, token, { maxRetries: 0 });
